import { useEffect, useRef, useState } from 'react';

import {
  getAttachmentFaces,
  getAttachmentStatus,
  PeopleTaggingStatus,
  type AttachmentFaceResponse
} from '@api/v4/private/people_tagging/faces';

const POLLING_INTERVAL_MS = 2000; // 2 seconds

interface UsePeopleTaggingFacesOptions {
  attachmentKey: string | null | undefined;
  brandfolderKey: string | null | undefined;
  enabled?: boolean; // Whether to start polling (e.g., when component is visible)
}

interface UsePeopleTaggingFacesResult {
  faces: AttachmentFaceResponse[];
  isLoading: boolean;
  status: PeopleTaggingStatus | null;
}

/**
 * Shared hook for polling people tagging faces based on attachment status.
 * Polls when status is WaitingForPeopleTagging, otherwise fetches faces once.
 */
export const usePeopleTaggingFaces = ({
  attachmentKey,
  brandfolderKey,
  enabled = true
}: UsePeopleTaggingFacesOptions): UsePeopleTaggingFacesResult => {
  const [faces, setFaces] = useState<AttachmentFaceResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<PeopleTaggingStatus | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fetchedAttachmentKeyRef = useRef<string | null>(null);
  const facesRef = useRef<AttachmentFaceResponse[]>([]);
  const statusRef = useRef<PeopleTaggingStatus | null>(null);

  useEffect(() => {
    // Don't start if not enabled or missing required keys
    if (!enabled || !attachmentKey || !brandfolderKey) {
      setFaces([]);
      facesRef.current = [];
      setIsLoading(false);
      setStatus(null);
      statusRef.current = null;
      fetchedAttachmentKeyRef.current = null;
      // Clear any existing polling
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      return;
    }

    // If we already have faces data for this attachment and status is not waiting, don't refetch
    if (
      facesRef.current.length > 0 &&
      fetchedAttachmentKeyRef.current === attachmentKey &&
      statusRef.current !== PeopleTaggingStatus.WaitingForPeopleTagging &&
      statusRef.current !== null
    ) {
      return;
    }

    // Clear any existing polling
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    const checkStatusAndLoadFaces = async (): Promise<void> => {
      try {
        setIsLoading(true);

        // First, check the attachment status
        const statusResponse = await getAttachmentStatus(attachmentKey);
        const currentStatus: PeopleTaggingStatus = statusResponse.data.attributes.status as PeopleTaggingStatus;

        setStatus(currentStatus);
        statusRef.current = currentStatus;

        // Only poll if status is waiting_for_people_tagging
        if (currentStatus === PeopleTaggingStatus.WaitingForPeopleTagging) {
          // Poll again if still waiting
          setFaces([]);
          facesRef.current = [];
          fetchedAttachmentKeyRef.current = null; // Reset since we're waiting
          pollingTimeoutRef.current = setTimeout(() => {
            void checkStatusAndLoadFaces();
          }, POLLING_INTERVAL_MS);
        } else {
          // For any other status (completed, failed, terminal, etc.), fetch faces
          const facesResponse = await getAttachmentFaces({
            attachmentKey,
            brandfolderKey
          });

          setFaces(facesResponse);
          facesRef.current = facesResponse;
          fetchedAttachmentKeyRef.current = attachmentKey;
          setIsLoading(false);
        }
      } catch {
        setFaces([]);
        facesRef.current = [];
        fetchedAttachmentKeyRef.current = null;
        setIsLoading(false);
        setStatus(null);
        statusRef.current = null;
      }
    };

    void checkStatusAndLoadFaces();

    // Cleanup polling on unmount or dependency change
    return (): void => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [attachmentKey, brandfolderKey, enabled]);

  return {
    faces,
    isLoading,
    status
  };
};
